import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { renewals } from './api';
import RenewalWorkspace, { NOTES_MAX, charCount } from './RenewalWorkspace';
import LinkToClientModal from './LinkToClientModal';
import { useAuth } from './AuthContext';
import { textMatches } from './searchText';   // 5.27.3 apostrophe-insensitive filters

// =============================================================================
// Helpers
// =============================================================================

const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDate = (d) => {
  if (!d) return '—';
  // Avoid timezone shift on YYYY-MM-DD strings
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, dd] = d.split('-').map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString();
  }
  return new Date(d).toLocaleDateString();
};

// v4.18: single source of truth for the Renewals "Address" column display string,
// used by both the on-screen table and the print template. Composes the raw
// policy-level parts with null guards so missing pieces leave no artifacts:
//   - number joins street with a space (no leading space when number is null)
//   - a comma precedes the CITY only when a street precedes it (no dangling comma
//     when city is null — a lone zip appends with a space, never a comma)
//   - zip appends with a space (no trailing space when zip is null)
// Returns null when nothing is present (call sites render an em-dash).
const formatAddress = (cycle) => {
  if (!cycle) return null;
  const clean = (s) => (s == null ? '' : String(s).trim());
  const street = [cycle.property_address_number, cycle.property_address_words]
    .map(clean).filter(Boolean).join(' ');
  const city = clean(cycle.property_city);
  const zip = clean(cycle.property_zip);
  let out = street;
  if (city) out = out ? `${out}, ${city}` : city;
  if (zip) out = out ? `${out} ${zip}` : zip;
  return out || null;
};

// ── Status palette (v4.21 change D) ─────────────────────────────────────────
// ONE source of truth per status hue: STATUS_RGB. Everything else — the solid
// pill / count-dot colour (STATUS_COLORS), the low-alpha whole-row tint
// (STATUS_ROW_TINT) and the pill TEXT colour (STATUS_PILL_TEXT) — is DERIVED from
// it, so re-hueing a status is a one-line tweak here and can never silently drop
// the label below AA.
//
// The row tint is intentionally LOW alpha: it composites over the row's real
// background (light #fff / dark #242424) rather than replacing it, so text stays
// legible in BOTH themes — a solid light fill would wash out dark-mode text.
//
// Contacted-yellow, quoted-gold and skipped-orange are deliberately ADJACENT
// hues; if they read too close on screen, nudge the RGBs below (nothing else
// changes — pill text re-picks itself). renewed-green (was #27AE60) and lost-red
// (was #A32D2D) are unchanged; contacted (was blue) and skipped (was grey) move
// onto this scheme; duplicate (brown) is new. pending stays neutral grey.
const STATUS_RGB = {
  contacted: [201, 162, 42],   // yellow
  quoted:    [184, 134, 11],   // darker gold
  renewed:   [39, 174, 96],    // green  (== old #27AE60)
  lost:      [163, 45, 45],    // red    (== old #A32D2D)
  skipped:   [210, 105, 30],   // orange
  duplicate: [121, 85, 72],    // brown
  rewritten: [103, 58, 183],   // 5.28.0: purple — rewritten to another company (terminal, read-only)
};
const PENDING_RGB = [136, 136, 136];   // #888 — neutral, no hue

const _rgb = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
const _rgba = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`;

// WCAG relative luminance + contrast, used to pick the pill TEXT colour that
// gives the higher contrast against each (derived) pill background. Because it is
// computed from STATUS_RGB, moving a hue automatically re-picks dark/white — a
// hardcoded map would go unreadable when a hue moves. Verified with the current
// hues (all 7 clear AA 4.5:1): pending 4.91, contacted 7.20, quoted 5.35,
// renewed 6.06, skipped 4.79 on dark #1A1A1A; lost 7.07, duplicate 6.55 on white.
const _srgbToLin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
const _relLum = ([r, g, b]) => 0.2126 * _srgbToLin(r) + 0.7152 * _srgbToLin(g) + 0.0722 * _srgbToLin(b);
const _contrast = (l1, l2) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
const PILL_DARK = '#1A1A1A';
const PILL_LIGHT = '#fff';
const _L_DARK = _relLum([26, 26, 26]);   // #1A1A1A
const _pillTextFor = (rgb) => (_contrast(_relLum(rgb), _L_DARK) >= _contrast(_relLum(rgb), 1) ? PILL_DARK : PILL_LIGHT);

// Solid colour: status <select> pill, read-only status span, count dots.
const STATUS_COLORS = {
  pending:   _rgb(PENDING_RGB),   // '#888' equivalent
  contacted: _rgb(STATUS_RGB.contacted),
  quoted:    _rgb(STATUS_RGB.quoted),
  renewed:   _rgb(STATUS_RGB.renewed),
  lost:      _rgb(STATUS_RGB.lost),
  skipped:   _rgb(STATUS_RGB.skipped),
  duplicate: _rgb(STATUS_RGB.duplicate),
  rewritten: _rgb(STATUS_RGB.rewritten),
};

// Whole-row tint keyed by status. pending is untinted (neutral). Per-hue alphas
// tuned so each reads at roughly equal strength over both themes.
const STATUS_ROW_TINT = {
  pending:   'transparent',
  contacted: _rgba(STATUS_RGB.contacted, 0.16),
  quoted:    _rgba(STATUS_RGB.quoted, 0.18),
  renewed:   _rgba(STATUS_RGB.renewed, 0.15),
  lost:      _rgba(STATUS_RGB.lost, 0.15),
  skipped:   _rgba(STATUS_RGB.skipped, 0.16),
  duplicate: _rgba(STATUS_RGB.duplicate, 0.20),
  rewritten: _rgba(STATUS_RGB.rewritten, 0.16),
};

// Pill TEXT colour, computed (never hardcoded) so a hue tweak stays AA-legible.
const STATUS_PILL_TEXT = {
  pending:   _pillTextFor(PENDING_RGB),
  contacted: _pillTextFor(STATUS_RGB.contacted),
  quoted:    _pillTextFor(STATUS_RGB.quoted),
  renewed:   _pillTextFor(STATUS_RGB.renewed),
  lost:      _pillTextFor(STATUS_RGB.lost),
  skipped:   _pillTextFor(STATUS_RGB.skipped),
  duplicate: _pillTextFor(STATUS_RGB.duplicate),
  rewritten: _pillTextFor(STATUS_RGB.rewritten),
};

const STATUS_LABELS = {
  pending: 'Pending',
  contacted: 'Contacted',
  quoted: 'Quoted',
  renewed: 'Renewed',
  lost: 'Lost',
  skipped: 'Skipped',
  duplicate: 'Duplicate',
  rewritten: 'Rewritten',   // 5.28.0: set only by Edit → Rewritten; never offered in the status dropdown
};
// 5.28.0: statuses a user may pick in the row's status dropdown ('rewritten' comes only from Edit → Rewritten).
const PICKABLE_STATUSES = Object.keys(STATUS_LABELS).filter((s) => s !== 'rewritten');

// =============================================================================
// v4.19: drag-resizable columns — ACTIVE LIST ONLY
// =============================================================================
// Scope note: this applies to the ActiveList table (ActiveList + CycleRow).
// The Lapsed and Late-Additions tables are deliberately untouched, and the
// print template is a *separate* portal'd <table className="renewals-print">
// whose 9 fixed column widths live in index.css @media print — it never reads
// this state, so on-screen dragging can't affect a printed sheet.
//
// Persistence is per-browser via localStorage (not per-user on the server):
// each staff member's machine keeps its own layout, which is exactly the
// desired granularity. The stored blob is merged OVER the defaults on load, so
// a column added by a future release picks up its default width instead of the
// whole saved layout being thrown away — and a corrupt/unparseable blob falls
// back to defaults silently.
const COL_WIDTHS_KEY = 'insuredocs.renewals.colWidths.v1';
const COL_WIDTHS_VERSION = 1;

// Column order here IS the on-screen column order; the <colgroup> and the
// <thead> cells are both generated from it, so they can never drift apart.
// CycleRow's <td>s must stay in this same order.
const ACTIVE_COLUMNS = [
  { id: 'date', label: 'Date' },
  { id: 'insured', label: 'Insured' },
  { id: 'address', label: 'Address' },
  { id: 'policy', label: 'Policy #' },
  { id: 'carrier', label: 'Carrier' },
  { id: 'premium', label: 'Premium', align: 'right' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'note', label: 'Note' },
  { id: 'status', label: 'Status' },
];

// Defaults chosen to reproduce the v4.18.0 auto-layout rendering as closely as
// possible: Address (170→160) and Note (220→175) track their old per-cell
// maxWidth caps, Assigned covers the reassign <select>'s 140px, and the rest
// track each column's max-content width at its font size. box-sizing is
// border-box app-wide (index.css line 1), so these include the 8px/10px cell
// padding. Sum = 1215 + MARKER_W, which still fits a 1280-wide viewport
// without horizontal scroll. These are also what "Reset widths" restores.
const DEFAULT_COL_WIDTHS = {
  date: 85,
  insured: 175,
  address: 160,
  policy: 105,
  carrier: 160,
  premium: 92,
  assigned: 155,
  note: 175,
  status: 108,
};

// Drag floor. Date / Premium / Status carry atomic values (a whole date, a
// currency amount, a status pill) that stay readable or not at all, so they get
// a higher floor than the generic text columns.
const MIN_COL_WIDTH = 50;
const MIN_COL_WIDTHS = { date: 70, premium: 70, status: 80 };
const minWidthFor = (id) => MIN_COL_WIDTHS[id] || MIN_COL_WIDTH;

// The trailing ⚠️-marker column is intentionally NOT resizable and carries no
// <col> width. Under table-layout:fixed the lone auto-width column absorbs
// whatever slack is left when the pane is wider than the sum of the set
// widths, so the header strip and panel background still span the full pane
// exactly as they did in v4.18.0 (a hard px table width would have left a bare
// gutter). MARKER_W only floors it, via the table's minWidth.
const MARKER_W = 28;

const tableMinWidth = (widths) =>
  ACTIVE_COLUMNS.reduce((sum, c) => sum + (widths[c.id] || 0), 0) + MARKER_W;

function loadColWidths() {
  try {
    const raw = localStorage.getItem(COL_WIDTHS_KEY);
    if (!raw) return { ...DEFAULT_COL_WIDTHS };
    const blob = JSON.parse(raw);
    const stored = (blob && typeof blob.widths === 'object' && blob.widths) || {};
    // Merge over defaults, one known column at a time: unknown keys are ignored
    // and missing/garbage values keep their default rather than nuking the blob.
    const out = { ...DEFAULT_COL_WIDTHS };
    for (const c of ACTIVE_COLUMNS) {
      const v = Number(stored[c.id]);
      if (Number.isFinite(v) && v > 0) out[c.id] = Math.max(minWidthFor(c.id), Math.round(v));
    }
    return out;
  } catch (e) {
    return { ...DEFAULT_COL_WIDTHS };
  }
}

function saveColWidths(widths) {
  try {
    localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify({ version: COL_WIDTHS_VERSION, widths }));
  } catch (e) {
    // Private mode / quota exceeded — widths simply don't persist this session.
  }
}

function clearColWidths() {
  try {
    localStorage.removeItem(COL_WIDTHS_KEY);
  } catch (e) { /* nothing to do */ }
}

// Shared clip treatment for the active list's text cells. Under table-layout:
// fixed a cell can no longer push its neighbours wider, so the only question is
// what a too-narrow column does to its content — this makes it truncate with an
// ellipsis (full value on the cell's title tooltip) rather than wrap the row
// into a ragged multi-line block.
const CELL_CLIP = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// v5.26.0: former employees. "Name (inactive)" wherever an assignee is shown, and
// a due date in bold red + a small "Due soon" tag for a former employee's open
// renewal that expires within 30 days (or is past due). Never a row fill — row
// fills are the status colours. due_soon is computed by the backend (Central date).
const DUE_RED = '#D63030';

const assigneeLabel = (c) => {
  if (c.assigned_user_name) {
    if (c.assigned_user_active === false) return c.assigned_user_name + ' (inactive)';
    if (c.assigned_user_hidden) return c.assigned_user_name + ' (hidden)';   // v5.27.0
    return c.assigned_user_name;
  }
  return c.assigned_employee_text || '—';
};

function DueDate({ date, due }) {
  return (
    <>
      <span style={due ? { color: DUE_RED, fontWeight: 700 } : undefined}>{fmtDate(date)}</span>
      {due && (
        <div style={{ marginTop: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: DUE_RED, borderRadius: 3, padding: '0 4px' }}>
            Due soon
          </span>
        </div>
      )}
    </>
  );
}

// --- v4.20 legacy-collapse: partition + inline badges (frontend only) --------
// A response is "collapse-annotated" iff the service set collapse_state on the
// rows (archive month + flag on). When it did NOT, every field is null and the
// list behaves exactly as before: MAIN == all rows, no extra sections.
function partitionCycles(cycles) {
  const all = cycles || [];
  const isDup = (c) => c && c.status === 'duplicate';
  const on = all.some(c => c && c.collapse_state != null);
  // v4.21 (change C): a cycle the owner marked 'duplicate' ALWAYS lands in HIDDEN,
  // regardless of collapse_state (or whether the response is collapse-annotated
  // at all), so it never shows in MAIN/VERIFY, never prints, and is never counted
  // — but stays visible & recoverable in the Hidden section.
  if (!on) {
    const dups = all.filter(isDup);
    return { isCollapse: false, MAIN: all.filter(c => !isDup(c)), VERIFY: [], HIDDEN: dups };
  }
  return {
    isCollapse: true,
    MAIN:   all.filter(c => !isDup(c) && c.collapse_state === 'kept' && (c.stale_years || 0) === 0),
    VERIFY: all.filter(c => !isDup(c) && c.collapse_state === 'kept' && (c.stale_years || 0) > 0),
    HIDDEN: all.filter(c => isDup(c) || c.collapse_state === 'suppressed'),
  };
}
// v5.6.0: statuses that count as a finish (server writes a dated notes file).
const FINISH_STATUSES = ['renewed', 'skipped', 'duplicate', 'lost', 'rewritten'];   // 5.28.0: + rewritten

// The MAIN slice of any cycles array (identity when not collapse-annotated).
const mainRowsOnly = (cycles) => partitionCycles(cycles).MAIN;

const BADGE_TONE = {
  warn:  { background: 'rgba(230,160,30,0.18)', color: '#B8860B', border: '1px solid rgba(230,160,30,0.45)' },
  alert: { background: 'rgba(200,60,60,0.18)',  color: '#C0392B', border: '1px solid rgba(200,60,60,0.45)' },
  muted: { background: 'rgba(130,130,130,0.15)', color: '#8A8A8A', border: '1px solid rgba(130,130,130,0.35)' },
};
function hiddenReasonLabel(reason) {
  if (!reason) return 'Hidden';
  const m = /^superseded_by_(\d{4})_term$/.exec(reason);
  if (m) return `Superseded by ${m[1]} term`;
  if (reason === 'same_year_duplicate') return 'Duplicate of a current-term policy';
  if (reason === 'null_effective_date') return 'No effective date on file';
  if (reason === 'term_invalid') return 'Bad dates on file';
  return reason;
}
// Inline badge for a row (null → none). Suppressed rows show the reason; kept
// rows show stale/term_invalid copy. Year from the row's own IED (YYYY-MM-DD).
function renewalBadge(cycle) {
  const st = cycle.collapse_state;
  if (st == null) return null;                       // active arm / flag off
  if (st === 'suppressed') return { text: hiddenReasonLabel(cycle.collapse_reason), tone: 'muted' };
  if (cycle.collapse_reason === 'term_invalid') return { text: 'Bad dates on file — check', tone: 'alert' };
  const yr = cycle.initial_effective_date ? String(cycle.initial_effective_date).slice(0, 4) : '';
  const s = cycle.stale_years || 0;
  if (s === 1) return { text: `Last term ${yr} — no current-year policy, verify`, tone: 'warn' };
  if (s >= 2) return { text: `Last term ${yr} — ${s} years stale, verify`, tone: 'warn' };
  return null;                                        // MAIN kept row — no badge
}

// ~6px grab zone straddling the right edge of a header cell. Pointer events +
// setPointerCapture so the drag survives the cursor leaving the handle (and
// leaving the window). Every handler stops propagation so a resize can never
// reach a future click/sort handler on the <th>.
function ColResizeHandle({ colId, widths, setWidths, accent }) {
  const [hot, setHot] = useState(false);

  const onPointerDown = (e) => {
    e.preventDefault();          // suppress the native text-selection drag
    e.stopPropagation();
    const startX = e.clientX;
    const startWidths = widths;
    const startW = startWidths[colId];
    const min = minWidthFor(colId);
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    // Track the committed width in the closure rather than reading it back out
    // of state on pointerup — state may not have re-rendered since the last
    // pointermove, which would persist a stale width.
    let lastW = startW;

    try { target.setPointerCapture(pointerId); } catch (err) { /* older browser */ }
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMove = (ev) => {
      const w = Math.max(min, Math.round(startW + (ev.clientX - startX)));
      if (w === lastW) return;
      lastW = w;
      setWidths((prev) => ({ ...prev, [colId]: w }));
    };
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      try { target.releasePointerCapture(pointerId); } catch (err) { /* already released */ }
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      setHot(false);
      saveColWidths({ ...startWidths, [colId]: lastW });
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  // Double-click the handle → reset just this column to its default.
  const onDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = { ...widths, [colId]: DEFAULT_COL_WIDTHS[colId] };
    setWidths(next);
    saveColWidths(next);
  };

  return (
    <span
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      title="Drag to resize · double-click to reset this column"
      style={{
        position: 'absolute', top: 0, right: -3, width: 6, height: '100%',
        cursor: 'col-resize', touchAction: 'none', userSelect: 'none', zIndex: 1,
        background: hot ? accent : 'transparent',
      }}
    />
  );
}

// Build a list of upcoming/recent month options for the picker.
// Default focus: the current month.
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  // Show 6 months back + 18 months forward (so next year's pre-entered renewals are visible)
  for (let i = 6; i >= -18; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
    });
  }
  return opts;
}

// LinkToClientModal moved to ./LinkToClientModal.jsx in v5.6.0 (the workspace uses it too).

// When the carrier pick-lists are filtered to active-only, a renewal whose
// carrier was later deactivated would otherwise lose its selected value (the
// option is gone from the list). Union the currently-selected carrier back in
// by id+name so it still displays and round-trips on save. This ONLY re-adds
// the one already-selected carrier; what is newly pickable is still governed by
// the active-only list. No-op when nothing is selected or no name is known.
function withSelectedCarrier(carriers, selectedId, selectedName) {
  if (!selectedId || !selectedName) return carriers;
  if (carriers.some((c) => String(c.id) === String(selectedId))) return carriers;
  return [...carriers, { id: selectedId, name: selectedName }];
}

// =============================================================================
// SearchableSelect — type-to-filter dropdown
// =============================================================================

export function SearchableSelect({ options, value, onChange, placeholder, darkMode, panelBg, borderColor, textPrimary, textSecondary, inputBg }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = React.useRef(null);

  // Find the selected option's label to display when not typing
  const selected = options.find(o => String(o.value) === String(value));
  const displayValue = open ? query : (selected ? selected.label : '');

  const filtered = !query
    ? options
    : options.filter(o => textMatches(o.label, query));   // 5.27.3 apostrophes ignored

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    background: inputBg, color: textPrimary,
    border: `1px solid ${borderColor}`, borderRadius: 5,
    cursor: open ? 'text' : 'pointer',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={displayValue}
        placeholder={placeholder || 'Type to search…'}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
          if (e.key === 'Enter' && filtered.length > 0) {
            onChange(filtered[0].value);
            setOpen(false);
            setQuery('');
          }
        }}
        style={inputStyle}
      />
      {selected && !open && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          title="Clear selection"
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', color: textSecondary,
            cursor: 'pointer', fontSize: 16, padding: '0 4px',
          }}
        >×</button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2,
          background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 5,
          maxHeight: 240, overflowY: 'auto', zIndex: 1100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: 12, color: textSecondary }}>
              No matches
            </div>
          ) : filtered.map(opt => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); setQuery(''); }}
              style={{
                padding: '8px 10px', fontSize: 13, cursor: 'pointer',
                background: String(opt.value) === String(value) ? '#185FA5' : 'transparent',
                color: String(opt.value) === String(value) ? '#fff' : textPrimary,
              }}
              onMouseEnter={(e) => {
                if (String(opt.value) !== String(value)) {
                  e.currentTarget.style.background = darkMode ? '#333' : '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (String(opt.value) !== String(value)) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// AddRenewalModal — right-side panel, similar to AddBonusModal
// =============================================================================

export function AddRenewalModal({ client, onClose, onSaved, darkMode }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const subPanelBg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';

  const [carriers, setCarriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // v5.27.0: assignee choices = the shared active group; staff keep "default to me" only (as before).
  const { user: me } = useAuth();
  const canPickAssignee = me?.role === 'admin' || me?.role === 'manager';

  // Auto-fill from client. Strip leading numeric prefix from client name.
  const stripPrefix = (s) => (s || '').replace(/^\d+\s+/, '').trim();
  const initialName = client ? stripPrefix(client.name) : '';

  const todayISO = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    named_insured: initialName,
    policy_number: '',
    carrier_id: '',
    initial_effective_date: todayISO,
    expiration_date: '',
    policy_kind: 'NEW POLICY',
    premium: '',
    primary_assigned_user_id: '',
    phone: client?.phone1 || '',
    email: client?.email || '',
    property_address_number: '',
    property_address_words: '',
    property_city: '',
    property_zip: '',
    is_mailing_address: null,
    notes: '',
  });

  useEffect(() => {
    renewals.listCarriers().then((r) => setCarriers(r.data || [])).catch(() => {});
    if (canPickAssignee) renewals.assignees().then((r) => setUsers(r.data?.active || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.named_insured.trim()) { setError('Named insured is required.'); return; }
    if (!form.initial_effective_date) { setError('Effective date is required.'); return; }
    if (!form.expiration_date) { setError('Policy expiration date is required.'); return; }

    setSaving(true);
    setError(null);
    try {
      await renewals.createPolicy({
        client_id: client?.id || null,
        named_insured: form.named_insured.trim(),
        policy_number: form.policy_number.trim() || null,
        carrier_id: form.carrier_id ? parseInt(form.carrier_id, 10) : null,
        initial_effective_date: form.initial_effective_date,
        policy_kind: form.policy_kind || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        property_address_number: form.property_address_number.trim() || null,
        property_address_words: form.property_address_words.trim() || null,
        property_city: form.property_city.trim() || null,
        property_zip: form.property_zip.trim() || null,
        is_mailing_address: form.is_mailing_address,
        primary_assigned_user_id: form.primary_assigned_user_id ? parseInt(form.primary_assigned_user_id, 10) : null,
        notes: form.notes.trim() || null,
        initial_premium: form.premium ? parseFloat(form.premium) : null,
        expiration_date: form.expiration_date,  // user-entered; required (client check + server 422 enforce it)
      });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ');
      }
      setError(msg || e.message || 'Failed to save');
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    background: inputBg, color: textPrimary,
    border: `1px solid ${borderColor}`, borderRadius: 5,
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 560,
      background: panelBg, color: textPrimary, zIndex: 1000,
      borderLeft: `1px solid ${borderColor}`,
      boxShadow: '-4px 0 16px rgba(0,0,0,0.2)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '14px 18px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#185FA5' }}>📋 Add Renewal</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} disabled={saving}
          style={{ padding: '6px 12px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save Renewal'}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
        {client && (
          <div style={{ background: subPanelBg, padding: 10, borderRadius: 5, marginBottom: 12, fontSize: 12, color: textSecondary }}>
            Client folder: <strong style={{ color: textPrimary }}>{client.name}</strong>
          </div>
        )}

        {error && (
          <div style={{
            background: '#A32D2D', color: '#fff', padding: '8px 12px',
            borderRadius: 5, fontSize: 12, marginBottom: 12,
          }}>{error}</div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Named Insured *</label>
          <input style={inputStyle} value={form.named_insured}
            onChange={(e) => update('named_insured', e.target.value)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Effective Date *</label>
            <input type="date" style={inputStyle} value={form.initial_effective_date}
              onChange={(e) => update('initial_effective_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Policy expiration date — required</label>
            <input type="date" style={inputStyle} value={form.expiration_date}
              onChange={(e) => update('expiration_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Policy Kind</label>
            <select style={inputStyle} value={form.policy_kind || ''}
              onChange={(e) => update('policy_kind', e.target.value)}>
              <option value="NEW POLICY">NEW POLICY</option>
              <option value="RENEWAL">RENEWAL</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Insurance Company *</label>
          <SearchableSelect
            options={withSelectedCarrier(carriers, form.carrier_id, form.carrier_name).map(c => ({ value: c.id, label: c.name }))}
            value={form.carrier_id}
            onChange={(v) => update('carrier_id', v)}
            placeholder="Type to search 100+ carriers…"
            darkMode={darkMode} panelBg={panelBg} borderColor={borderColor}
            textPrimary={textPrimary} textSecondary={textSecondary} inputBg={inputBg}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Policy Number</label>
            <input style={inputStyle} value={form.policy_number}
              onChange={(e) => update('policy_number', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Premium</label>
            <input type="number" step="0.01" style={inputStyle} value={form.premium}
              onChange={(e) => update('premium', e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Assigned Employee</label>
          <SearchableSelect
            options={users.map(u => ({ value: u.id, label: u.full_name }))}
            value={form.primary_assigned_user_id}
            onChange={(v) => update('primary_assigned_user_id', v)}
            placeholder="— Default to me — (or type to find)"
            darkMode={darkMode} panelBg={panelBg} borderColor={borderColor}
            textPrimary={textPrimary} textSecondary={textSecondary} inputBg={inputBg}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={form.phone}
              onChange={(e) => update('phone', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={form.email}
              onChange={(e) => update('email', e.target.value)} />
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>Property Address</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, marginBottom: 8 }}>
            <input placeholder="#" style={inputStyle} value={form.property_address_number}
              onChange={(e) => update('property_address_number', e.target.value)} />
            <input placeholder="Street name" style={inputStyle} value={form.property_address_words}
              onChange={(e) => update('property_address_words', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 8 }}>
            <input placeholder="City" style={inputStyle} value={form.property_city}
              onChange={(e) => update('property_city', e.target.value)} />
            <input placeholder="ZIP" style={inputStyle} value={form.property_zip}
              onChange={(e) => update('property_zip', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: textSecondary }}>
            <span>Same as mailing address?</span>
            <label><input type="radio" name="mail" checked={form.is_mailing_address === true}
              onChange={() => update('is_mailing_address', true)} /> Yes</label>
            <label><input type="radio" name="mail" checked={form.is_mailing_address === false}
              onChange={() => update('is_mailing_address', false)} /> No</label>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={form.notes}
            onChange={(e) => update('notes', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Renewal modal (v4.3) — replaces the old confirm-dialog roll-forward.
// Selecting status 'renewed' opens this. On save it calls /renew, which
// atomically marks the source cycle renewed AND creates the next linked term
// (Option B). All current-term fields editable; next expiration required.
// =============================================================================

function daysBetweenISO(a, b) {
  const [ay, am, ad] = a.slice(0, 10).split('-').map(Number);
  const [by, bm, bd] = b.slice(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

const LONG_GAP_DAYS = 548; // ~18 months; mirrors backend soft-warning threshold

function RenewalModal({ cycle, users, darkMode, onClose, onSaved }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';

  // The current term's expiration anchors everything. Fall back to renewal_date
  // only defensively (e.g. if the backend hasn't been reloaded with expiration_date yet).
  const curExp = (cycle.expiration_date || cycle.renewal_date || '').slice(0, 10);

  const [carriers, setCarriers] = useState([]);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    named_insured: cycle.named_insured || '',
    policy_number: cycle.policy_number || '',
    carrier_id: '',                                  // filled once the source policy loads
    premium: cycle.premium ?? '',
    assigned_user_id: cycle.assigned_user_id || '',
    effective_date: curExp,                          // DECISION: new term begins at the old expiration
    next_expiration_date: '',                        // no auto-fill — user enters the next term's expiration
    notes: cycle.outcome_notes || '',                // shared outcome note: prefill so a renew doesn't wipe existing context
  });

  useEffect(() => {
    renewals.listCarriers().then((r) => setCarriers(r.data || [])).catch(() => {});
    // Fetch the source policy for the authoritative carrier_id (the cycle row
    // only carries carrier_name, not the id needed to pre-select the dropdown).
    renewals.getPolicy(cycle.policy_id)
      .then((r) => setForm((f) => ({ ...f, carrier_id: r.data?.carrier_id || '' })))
      .catch(() => {})
      .finally(() => setLoadingPolicy(false));
  }, [cycle.policy_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Client validation mirrors the backend.
  const missingRequired = !form.named_insured.trim() || !form.effective_date || !form.next_expiration_date;
  const notStrictlyAfter = !!(form.next_expiration_date && curExp && !(form.next_expiration_date > curExp));
  const longGap = !!(form.next_expiration_date && curExp
    && daysBetweenISO(curExp, form.next_expiration_date) > LONG_GAP_DAYS);
  const notesCount = charCount(form.notes.trim());       // v5.6.0: 5,000-char cap (server counts the same way)
  const notesOver = notesCount > NOTES_MAX;
  const blockSave = saving || loadingPolicy || missingRequired || notStrictlyAfter || notesOver;

  const handleSave = async () => {
    if (missingRequired) { setError('Named insured, effective date, and next expiration date are required.'); return; }
    if (notesOver) { setError(`Renewal notes are limited to ${NOTES_MAX.toLocaleString('en-US')} characters.`); return; }
    if (notStrictlyAfter) { setError(`Next expiration date must be after the current expiration date (${curExp}).`); return; }
    setSaving(true);
    setError(null);
    try {
      const resp = await renewals.renewCycle(cycle.id, {
        next_expiration_date: form.next_expiration_date,
        named_insured: form.named_insured.trim(),
        policy_number: form.policy_number.trim() || null,
        carrier_id: form.carrier_id ? parseInt(form.carrier_id, 10) : null,
        premium: form.premium === '' ? null : parseFloat(form.premium),
        assigned_user_id: form.assigned_user_id ? parseInt(form.assigned_user_id, 10) : null,
        effective_date: form.effective_date,
        notes: form.notes.trim() || null,
      });
      if (resp?.data?.created === false) {
        // Idempotent re-submit: the next term already existed — treat as success.
        console.info('Renewal already existed; returned existing successor.');
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ');
      }
      setError(msg || e.message || 'Failed to renew');
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    background: inputBg, color: textPrimary,
    border: `1px solid ${borderColor}`, borderRadius: 5,
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 560,
      background: panelBg, color: textPrimary, zIndex: 1000,
      borderLeft: `1px solid ${borderColor}`,
      boxShadow: '-4px 0 16px rgba(0,0,0,0.2)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '14px 18px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#27AE60' }}>♻️ Renew Policy</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} disabled={saving}
          style={{ padding: '6px 12px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={blockSave}
          style={{ padding: '6px 14px', background: '#27AE60', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: blockSave ? 'not-allowed' : 'pointer', opacity: blockSave ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save Renewal'}
        </button>
      </div>

      <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && (
          <div style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: 12 }}>{error}</div>
        )}

        <div style={{ fontSize: 12, color: textSecondary }}>
          Renewing <strong style={{ color: textPrimary }}>{cycle.named_insured}</strong>
          {cycle.carrier_name ? ` · ${cycle.carrier_name}` : ''} · current expiration{' '}
          <strong style={{ color: textPrimary }}>{fmtDate(curExp)}</strong>.
          Saving marks this cycle <em>renewed</em> and creates the next term.
        </div>

        <div>
          <label style={labelStyle}>Named insured *</label>
          <input style={inputStyle} value={form.named_insured} onChange={(e) => update('named_insured', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Policy number</label>
            <input style={inputStyle} value={form.policy_number} onChange={(e) => update('policy_number', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Carrier</label>
            <select style={inputStyle} value={form.carrier_id} onChange={(e) => update('carrier_id', e.target.value)} disabled={loadingPolicy}>
              <option value="">{loadingPolicy ? 'Loading…' : '— None —'}</option>
              {withSelectedCarrier(carriers, form.carrier_id, cycle.carrier_name).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Premium</label>
            <input type="number" step="0.01" style={inputStyle} value={form.premium} onChange={(e) => update('premium', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Assignee</label>
            <select style={inputStyle} value={form.assigned_user_id} onChange={(e) => update('assigned_user_id', e.target.value)}>
              <option value="">— Unassigned —</option>
              {/* v5.27.0: a hidden / former assignee isn't pickable — show them greyed, not "Unassigned" */}
              {cycle.assigned_user_id && !users.some(u => u.id === cycle.assigned_user_id) && (
                <option value={cycle.assigned_user_id} disabled>{assigneeLabel(cycle)}</option>
              )}
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>New term effective date *</label>
            <input type="date" style={inputStyle} value={form.effective_date} onChange={(e) => update('effective_date', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Next policy expiration date — required</label>
            <input type="date" style={{ ...inputStyle, borderColor: notStrictlyAfter ? '#A32D2D' : borderColor }}
              value={form.next_expiration_date} onChange={(e) => update('next_expiration_date', e.target.value)} />
          </div>
        </div>

        {notStrictlyAfter && (
          <div style={{ color: '#A32D2D', fontSize: 12, fontWeight: 600 }}>
            Next expiration must be after the current expiration ({fmtDate(curExp)}).
          </div>
        )}
        {!notStrictlyAfter && longGap && (
          <div style={{ color: '#F59E0B', fontSize: 12, fontWeight: 600 }}>
            ⚠ Next expiration is more than ~18 months out — double-check the year. (You can still save.)
          </div>
        )}

        <div>
          <label style={labelStyle}>Renewal notes</label>
          <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', borderColor: notesOver ? '#C0392B' : borderColor }} value={form.notes} onChange={(e) => update('notes', e.target.value)} />
          <div style={{ fontSize: 11, textAlign: 'right', marginTop: 2, color: notesOver ? '#C0392B' : textSecondary }}>
            {notesCount.toLocaleString('en-US')} / {NOTES_MAX.toLocaleString('en-US')}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 5.28.0 — Edit popup (Henry 2026-10-20): (a) Correction / mid-term change, (b) Rewritten (company change).
// Fields are prefilled; Save first asks the server what it would do (month move, later terms, split, new renewal's
// month) and shows it; "Confirm" then saves. Every user may edit (the server applies the restricted-client rule).
// =============================================================================

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

/** 'March 2027' for an ISO date (YYYY-MM-DD…); '' when blank. */
export function monthLabelISO(iso) {
  const m = /^(\d{4})-(\d{2})/.exec(iso || '');
  return m ? `${MONTH_NAMES[parseInt(m[2], 10) - 1]} ${m[1]}` : '';
}

/** The live month line under the dates. correction: "This will move the renewal from March 2027 to June 2027"
 *  (null when the month stays); rewritten: "The new renewal will list in June 2027". */
export function monthMoveNotice(mode, oldExp, newExp) {
  const from = monthLabelISO(oldExp);
  const to = monthLabelISO(newExp);
  if (!to) return null;
  if (mode === 'rewritten') return `The new renewal will list in ${to}`;
  if (mode === 'correction' && from && from !== to) return `This will move the renewal from ${from} to ${to}`;
  return null;
}

export function RenewalEditModal({ cycle, darkMode, onClose, onSaved }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';
  const curExp = (cycle.expiration_date || cycle.renewal_date || '').slice(0, 10);

  const [mode, setMode] = useState(null);                 // 'correction' | 'rewritten' — chosen first
  const [carriers, setCarriers] = useState([]);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [preview, setPreview] = useState(null);           // server preview of the current form + mode
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    named_insured: cycle.named_insured || '',
    property_address_number: cycle.property_address_number || '',
    property_address_words: cycle.property_address_words || '',
    property_city: cycle.property_city || '',
    property_zip: cycle.property_zip || '',
    policy_number: cycle.policy_number || '',
    carrier_id: '',
    effective_date: (cycle.initial_effective_date || '').slice(0, 10),
    expiration_date: curExp,
    premium: cycle.premium ?? '',
  });

  useEffect(() => {
    renewals.listCarriers().then((r) => setCarriers(r.data || [])).catch(() => {});
    renewals.getPolicy(cycle.policy_id)
      .then((r) => setForm((f) => ({ ...f, carrier_id: r.data?.carrier_id || '' })))
      .catch(() => {})
      .finally(() => setLoadingPolicy(false));
  }, [cycle.policy_id]);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setPreview(null); };
  const pickMode = (m) => { setMode(m); setPreview(null); setError(null); };

  const body = () => ({
    mode,
    named_insured: form.named_insured.trim(),
    property_address_number: form.property_address_number.trim() || null,
    property_address_words: form.property_address_words.trim() || null,
    property_city: form.property_city.trim() || null,
    property_zip: form.property_zip.trim() || null,
    policy_number: form.policy_number.trim() || null,
    carrier_id: form.carrier_id ? parseInt(form.carrier_id, 10) : null,
    effective_date: form.effective_date || null,
    expiration_date: form.expiration_date || null,
    premium: form.premium === '' || form.premium === null ? null : form.premium,
  });

  const errText = (e, fallback) => {
    let msg = e.response?.data?.detail;
    if (Array.isArray(msg)) msg = msg.map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ');
    return msg || e.message || fallback;
  };

  const datesBad = !!(form.effective_date && form.expiration_date && !(form.expiration_date > form.effective_date));
  const missing = !form.named_insured.trim() || !form.effective_date || !form.expiration_date
    || (mode === 'rewritten' && !form.carrier_id);
  const blockSave = busy || loadingPolicy || !mode || missing || datesBad;
  const notice = mode ? monthMoveNotice(mode, curExp, form.expiration_date) : null;

  const review = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await renewals.previewEdit(cycle.id, body());
      setPreview(r.data);
    } catch (e) {
      setError(errText(e, 'Could not check the change'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await renewals.editCycle(cycle.id, body());
      if (onSaved) onSaved(r.data);
      onClose();
    } catch (e) {
      setError(errText(e, 'Failed to save'));
      setBusy(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, background: inputBg, color: textPrimary,
    border: `1px solid ${borderColor}`, borderRadius: 5 };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 4, display: 'block' };
  const modeBtn = (m, label, hint) => (
    <button type="button" data-testid={`edit-mode-${m}`} onClick={() => pickMode(m)} aria-pressed={mode === m}
      style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
        background: mode === m ? (m === 'rewritten' ? STATUS_ROW_TINT.rewritten : 'rgba(24,95,165,0.12)') : panelBg,
        color: textPrimary, border: `2px solid ${mode === m ? (m === 'rewritten' ? STATUS_COLORS.rewritten : '#185FA5') : borderColor}` }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>{hint}</div>
    </button>
  );
  const isRw = mode === 'rewritten';

  return (
    <div data-testid="renewal-edit-modal" style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 600, background: panelBg, color: textPrimary,
      zIndex: 1000, borderLeft: `1px solid ${borderColor}`, boxShadow: '-4px 0 16px rgba(0,0,0,0.2)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>✎ Edit renewal</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} disabled={busy}
          style={{ padding: '6px 12px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
          Cancel
        </button>
        {preview ? (
          <button data-testid="edit-confirm" onClick={confirm} disabled={busy}
            style={{ padding: '6px 14px', background: isRw ? STATUS_COLORS.rewritten : '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        ) : (
          <button data-testid="edit-save" onClick={review} disabled={blockSave}
            style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: blockSave ? 'not-allowed' : 'pointer', opacity: blockSave ? 0.6 : 1 }}>
            {busy ? 'Checking…' : 'Save…'}
          </button>
        )}
      </div>

      <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: 12 }}>{error}</div>}
        <div style={{ fontSize: 12, color: textSecondary }}>
          <strong style={{ color: textPrimary }}>{cycle.named_insured}</strong>
          {cycle.carrier_name ? ` · ${cycle.carrier_name}` : ''}{cycle.policy_number ? ` ${cycle.policy_number}` : ''} · expires{' '}
          <strong style={{ color: textPrimary }}>{fmtDate(curExp)}</strong> · {STATUS_LABELS[cycle.status] || cycle.status}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {modeBtn('correction', 'Correction / mid-term change', 'Fix this renewal in place — this term and later terms. Status stays.')}
          {modeBtn('rewritten', 'Rewritten (company change)', 'Retire this renewal as Rewritten and create a new one with the new company.')}
        </div>
        {!mode && <div data-testid="edit-pick-mode" style={{ fontSize: 12, color: textSecondary }}>Choose what kind of change this is.</div>}

        {mode && (<>
          <div>
            <label style={labelStyle}>Named insured *</label>
            <input style={inputStyle} value={form.named_insured} onChange={(e) => update('named_insured', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 90 }}><label style={labelStyle}>Number</label>
              <input style={inputStyle} value={form.property_address_number} onChange={(e) => update('property_address_number', e.target.value)} /></div>
            <div style={{ flex: 2 }}><label style={labelStyle}>Street</label>
              <input style={inputStyle} value={form.property_address_words} onChange={(e) => update('property_address_words', e.target.value)} /></div>
            <div style={{ flex: 1 }}><label style={labelStyle}>City</label>
              <input style={inputStyle} value={form.property_city} onChange={(e) => update('property_city', e.target.value)} /></div>
            <div style={{ width: 80 }}><label style={labelStyle}>ZIP</label>
              <input style={inputStyle} value={form.property_zip} onChange={(e) => update('property_zip', e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{isRw ? 'New policy number' : 'Policy number'}</label>
              <input style={inputStyle} value={form.policy_number} onChange={(e) => update('policy_number', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{isRw ? 'New carrier *' : 'Carrier'}</label>
              <select style={inputStyle} value={form.carrier_id} onChange={(e) => update('carrier_id', e.target.value)} disabled={loadingPolicy}>
                <option value="">{loadingPolicy ? 'Loading…' : '— None —'}</option>
                {withSelectedCarrier(carriers, form.carrier_id, cycle.carrier_name).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Effective date *</label>
              <input type="date" style={inputStyle} value={form.effective_date} onChange={(e) => update('effective_date', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Expiration date *</label>
              <input type="date" data-testid="edit-expiration" style={{ ...inputStyle, borderColor: datesBad ? '#A32D2D' : borderColor }}
                value={form.expiration_date} onChange={(e) => update('expiration_date', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{isRw ? 'Premium (new)' : 'Premium (this term only)'}</label>
              <input type="number" step="0.01" style={inputStyle} value={form.premium} onChange={(e) => update('premium', e.target.value)} />
            </div>
          </div>
          {datesBad && <div style={{ color: '#A32D2D', fontSize: 12, fontWeight: 600 }}>The expiration date must be after the effective date.</div>}
          {notice && !datesBad && (
            <div data-testid="edit-month-notice" style={{ fontSize: 12, fontWeight: 600, color: isRw ? STATUS_COLORS.rewritten : '#185FA5' }}>{notice}</div>
          )}
          <div style={{ fontSize: 11, color: textSecondary }}>
            {isRw
              ? 'This renewal and every later term of it become Rewritten (read-only). The new renewal keeps the client link, assignee, notes and Alternate Options, and starts as Pending. A note is added to both.'
              : 'Policy #, carrier, insured, address and the date shift carry to every later term. Earlier terms never change. A note with old → new is added.'}
          </div>
          {preview && (
            <div data-testid="edit-preview" style={{ border: `1px solid ${borderColor}`, borderRadius: 6, padding: '10px 12px', fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{preview.notice}</div>
              {preview.late_addition && <div style={{ color: '#B45309', marginBottom: 6 }}>⚠️ That month is already finalized — it will show as a late addition.</div>}
              {(preview.changes || []).map((c) => (
                <div key={c.field}>{c.field}: <span style={{ color: textSecondary }}>{c.old}</span> → <strong>{c.new}</strong></div>
              ))}
              <div style={{ marginTop: 6, color: textSecondary }}>Check it, then press Confirm.</div>
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

// =============================================================================
// Renewal Cycle Row — single row in the list
// =============================================================================

export function CycleRow({ cycle, users, canManage, user, onChange, openClientFolder, darkMode, panelBg, borderColor, textPrimary, textSecondary, readOnly = false, onClientTreesChanged }) {
  const [editingPremium, setEditingPremium] = useState(false);
  const [premiumDraft, setPremiumDraft] = useState(cycle.premium ?? '');
  const [linkingPolicyId, setLinkingPolicyId] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);   // v5.6.0: status-only confirm (Henry #1)
  const [showWorkspace, setShowWorkspace] = useState(false);  // v5.6.0: notes pop-out
  const [showEdit, setShowEdit] = useState(false);            // 5.28.0: Edit popup
  const [undoing, setUndoing] = useState(false);

  const statusColor = STATUS_COLORS[cycle.status] || '#888';
  const pillText = STATUS_PILL_TEXT[cycle.status] || '#fff';   // v4.21 (change D): AA-picked pill text
  const rowTint = STATUS_ROW_TINT[cycle.status] || 'transparent';   // v4.21 (change D): low-alpha whole-row tint
  const badge = renewalBadge(cycle);   // v4.20 inline collapse badge (null on active arm)

  // Edit permission: admin/manager can edit anything; staff can only edit their own
  const isOwn = cycle.assigned_user_id === user?.id;
  // v4.20: readOnly (HIDDEN section) forces the row inert — status dropdown,
  // premium input and note editor all fall back to their read-only spans —
  // regardless of ownership. Reading and the folder link are unaffected.
  // 5.28.0: a rewritten row is read-only too (status, premium, assignee, link, Edit, Renew); Undo is admin-only.
  const isRewritten = cycle.status === 'rewritten';
  const canEditThisRow = !readOnly && !isRewritten && (canManage || isOwn);
  const rw = cycle.rewrite || null;
  const isAdmin = user?.role === 'admin';
  const undoRewrite = async () => {
    if (!rw || rw.undo_block || undoing) return;
    if (!window.confirm(`Undo this rewrite? The new ${rw.role === 'old' ? rw.other_label : cycle.carrier_name || ''} renewal is removed and the old renewal (and its later terms) go back to their previous status.`)) return;
    setUndoing(true);
    try {
      await renewals.undoRewrite(rw.rewrite_id);
      if (onChange) onChange();
    } catch (e) {
      alert('Undo failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUndoing(false);
    }
  };

  // v4.16: a completed renewal (renewed AND its next term exists) locks the
  // status dropdown — un-renewing isn't supported (backend 409s too). A
  // still-pending prior-month cycle is NOT locked, so late renewals still work.
  const isLockedRenewed = cycle.status === 'renewed' && cycle.has_successor;
  const renewedTooltip = 'Renewed' + (cycle.outcome_at ? ' ' + fmtDate(cycle.outcome_at) : '')
    + (cycle.successor_expiration ? ` — next term ${fmtDate(cycle.successor_expiration)}` : ' — next term created');

  const onStatusChange = async (newStatus) => {
    // v4.3: renewing is no longer fire-and-confirm. Selecting 'renewed' opens the
    // Renewal modal, which atomically marks THIS cycle renewed AND creates the
    // next linked term on save (Option B). We intentionally write NO status here:
    // the controlled <select value={cycle.status}> snaps back to the current
    // status while the modal is open, so Cancel leaves the row unchanged.
    if (newStatus === 'renewed') {
      setShowRenewModal(true);
      return;
    }
    // v5.6.0 (Henry #1): status only — a small Save/Cancel, no note box. Nothing is
    // written until Save (Cancel snaps the controlled <select> back to cycle.status).
    // Notes are edited in the workspace (PATCH /notes), never through /status.
    if (newStatus === cycle.status) return;
    setPendingStatus(newStatus);
  };

  const commitStatus = async () => {
    try {
      await renewals.updateCycleStatus(cycle.id, { status: pendingStatus });
      // v5.6.0: a finish (lost/skipped/duplicate) on a linked renewal writes a notes file
      // and may create Renewals/<year> folders -> tell Main to refresh that client's tree.
      if (FINISH_STATUSES.includes(pendingStatus) && cycle.client_id && onClientTreesChanged) onClientTreesChanged([cycle.client_id]);
      setPendingStatus(null);
      if (onChange) onChange();
    } catch (e) {
      alert('Failed to update status: ' + (e.response?.data?.detail || e.message));
    }
  };

  const onAssignChange = async (newUserId) => {
    try {
      await renewals.reassignCycle(cycle.id, { assigned_user_id: newUserId ? parseInt(newUserId, 10) : null });
      if (onChange) onChange();
    } catch (e) {
      alert('Failed to reassign: ' + (e.response?.data?.detail || e.message));
    }
  };

  const savePremium = async () => {
    try {
      const v = premiumDraft === '' ? null : parseFloat(premiumDraft);
      await renewals.updateCyclePremium(cycle.id, { premium: v });
      setEditingPremium(false);
      if (onChange) onChange();
    } catch (e) {
      alert('Failed to update premium: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <tr style={{ borderTop: `1px solid ${borderColor}`, background: rowTint }}>
      {/* v4.19: widths now come from the <colgroup> (table-layout: fixed), so
          the per-cell maxWidth caps are gone. Text columns clip with an
          ellipsis and expose the full value on hover, so narrowing a column
          truncates a row instead of breaking it. */}
      <td style={{ padding: '8px 10px', fontSize: 12, ...CELL_CLIP }}>
        <DueDate date={cycle.renewal_date} due={cycle.due_soon} />
      </td>
      <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600, ...CELL_CLIP }}
        title={cycle.named_insured || undefined}>
        {cycle.client_id ? (
          <span>
            <span style={{ color: '#5BA3D9', cursor: 'pointer' }}
              onClick={() => openClientFolder && openClientFolder(cycle.client_id)}>
              {cycle.named_insured}
            </span>
            {!readOnly && !isRewritten && <button
              onClick={(e) => { e.stopPropagation(); setLinkingPolicyId(cycle.policy_id); }}
              title="Change linked client folder"
              style={{
                fontSize: 10, color: textSecondary,
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '0 4px', marginLeft: 4,
              }}
            >✎</button>}
          </span>
        ) : (
          <span>
            {cycle.named_insured}{' '}
            {!readOnly && !isRewritten && <button
              onClick={(e) => { e.stopPropagation(); setLinkingPolicyId(cycle.policy_id); }}
              title="Link this renewal to a client folder"
              style={{
                fontSize: 10, color: '#5BA3D9',
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '0 2px',
              }}
            >🔗 (no folder)</button>}
          </span>
        )}
        {badge && (
          <div title={badge.text} style={{
            display: 'block', marginTop: 3, whiteSpace: 'normal',
            fontSize: 10, lineHeight: 1.25, fontWeight: 600,
            padding: '1px 6px', borderRadius: 3, ...BADGE_TONE[badge.tone],
          }}>
            {badge.text}
          </div>
        )}
        {rw && (
          <div data-testid={`rewrite-link-${cycle.id}`} style={{ marginTop: 3, fontSize: 10, fontWeight: 600, whiteSpace: 'normal',
            color: STATUS_COLORS.rewritten }}>
            {rw.role === 'old'
              ? `↪ Rewritten to ${rw.other_label}${rw.other_month ? ` (${rw.other_month})` : ''}`
              : `↩ Rewritten from ${rw.other_label}`}
            {isAdmin && (
              <button onClick={undoRewrite} disabled={!!rw.undo_block || undoing} title={rw.undo_block || 'Undo this rewrite'}
                data-testid={`undo-rewrite-${cycle.id}`}
                style={{ marginLeft: 6, fontSize: 10, padding: '0 6px', borderRadius: 3, cursor: rw.undo_block ? 'not-allowed' : 'pointer',
                  background: 'transparent', color: rw.undo_block ? textSecondary : STATUS_COLORS.rewritten,
                  border: `1px solid ${rw.undo_block ? borderColor : STATUS_COLORS.rewritten}` }}>
                {undoing ? 'Undoing…' : 'Undo rewrite'}
              </button>
            )}
            {isAdmin && rw.undo_block && (
              <div style={{ fontWeight: 400, color: textSecondary }}>Undo unavailable: {rw.undo_block}</div>
            )}
          </div>
        )}
      </td>
      {/* v4.18: Address — secondary disambiguator, styled smaller/muted so the
          Insured name stays the anchor. Truncates with ellipsis; hover shows full.
          v4.19: maxWidth cap dropped (superseded by the colgroup); this was the
          pattern the other text columns now follow. */}
      <td style={{ padding: '8px 10px', fontSize: 11, color: textSecondary, ...CELL_CLIP }}
        title={formatAddress(cycle) || undefined}>
        {formatAddress(cycle) || '—'}
      </td>
      <td style={{ padding: '8px 10px', fontSize: 12, color: textSecondary, ...CELL_CLIP }}
        title={cycle.policy_number || undefined}>
        {cycle.policy_number || '—'}
      </td>
      {/* v4.18 capped Carrier at 200px and wrapped it; v4.19 supersedes that with
          the draggable column width + the shared ellipsis-and-tooltip treatment,
          so rows keep a uniform height at any width. Print still wraps. */}
      <td style={{ padding: '8px 10px', fontSize: 12, ...CELL_CLIP }}
        title={cycle.carrier_name || undefined}>
        {cycle.carrier_name || '—'}
      </td>
      <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', ...CELL_CLIP }}>
        {editingPremium ? (
          <input type="number" step="0.01" autoFocus
            value={premiumDraft}
            onChange={(e) => setPremiumDraft(e.target.value)}
            onBlur={savePremium}
            onKeyDown={(e) => { if (e.key === 'Enter') savePremium(); if (e.key === 'Escape') setEditingPremium(false); }}
            style={{ width: 90, maxWidth: '100%', padding: '4px 6px', fontSize: 12, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 3 }}
          />
        ) : (
          <span
            style={{ cursor: canEditThisRow ? 'pointer' : 'default', opacity: canEditThisRow ? 1 : 0.7 }}
            onClick={() => canEditThisRow && setEditingPremium(true)}
            title={canEditThisRow ? 'Click to edit' : isRewritten ? 'Read-only — rewritten to another company' : 'Read-only — assigned to another employee'}
          >
            {fmtMoney(cycle.premium)}
          </span>
        )}
      </td>
      <td style={{ padding: '8px 10px', fontSize: 12, ...CELL_CLIP }}
        title={(cycle.assigned_user_name || cycle.assigned_employee_text) ? assigneeLabel(cycle) : undefined}>
        {canManage && !isRewritten ? (
          <select value={cycle.assigned_user_id || ''}
            onChange={(e) => onAssignChange(e.target.value)}
            // v4.19: width+maxWidth:100% so the control shrinks with the column
            // instead of being clipped by the cell.
            style={{ padding: '3px 6px', fontSize: 11, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 3, width: 140, maxWidth: '100%' }}>
            <option value="">— Unassigned —</option>
            {/* v5.26.0: a former employee isn't in the active list — show them, not "Unassigned" */}
            {cycle.assigned_user_id && !users.some(u => u.id === cycle.assigned_user_id) && (
              <option value={cycle.assigned_user_id} disabled>{assigneeLabel(cycle)}</option>
            )}
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        ) : (
          <span>{assigneeLabel(cycle)}</span>
        )}
        {cycle.assigned_employee_text && !cycle.assigned_user_id && (
          <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 2 }}>
            Was: {cycle.assigned_employee_text} (former)
          </div>
        )}
      </td>
      {/* Note keeps its wrapping (free text, and the inline editor lives here);
          v4.19 drops the 220px cap and clips to the dragged column width so a
          long note can never spill onto the Status cell. */}
      <td style={{ padding: '8px 10px', fontSize: 12, color: textSecondary,
        overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {/* v5.6.0: note preview + 📎 file count; click opens the workspace (all users —
            Hidden/read-only rows open read-only). Restricted rows: the server nulls the
            note fields for non-admins, so nothing shows here. */}
        <span data-testid={`note-cell-${cycle.id}`} onClick={() => setShowWorkspace(true)}
          title={readOnly ? 'View notes (read-only)' : 'Open notes'}
          style={{ cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'baseline', maxWidth: '100%' }}>
          {cycle.note_preview ? (
            <span style={{ color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cycle.note_preview}</span>
          ) : readOnly ? (
            <span style={{ opacity: 0.4 }}>—</span>
          ) : (
            <span style={{ opacity: 0.5, fontSize: 11 }}>— add note —</span>
          )}
          {cycle.file_count > 0 && (
            <span title={`${cycle.file_count} renewal document${cycle.file_count === 1 ? '' : 's'}`}
              style={{ fontSize: 11, color: textSecondary, whiteSpace: 'nowrap' }}>📎 {cycle.file_count}</span>
          )}
        </span>
      </td>
      <td style={{ padding: '8px 10px', fontSize: 12, overflow: 'hidden' }}>
        {pendingStatus ? (
          <div data-testid={`status-confirm-${cycle.id}`}>
            <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>
              → {STATUS_LABELS[pendingStatus] || pendingStatus}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={commitStatus}
                style={{ fontSize: 10, padding: '2px 8px', background: STATUS_COLORS[pendingStatus] || statusColor,
                  color: STATUS_PILL_TEXT[pendingStatus] || pillText, border: 'none', borderRadius: 3, cursor: 'pointer' }}>Save</button>
              <button onClick={() => setPendingStatus(null)}
                style={{ fontSize: 10, padding: '2px 8px', background: 'transparent', color: textSecondary,
                  border: `1px solid ${borderColor}`, borderRadius: 3, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : isRewritten ? (
          <span
            style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600,
              background: statusColor, color: pillText, borderRadius: 3, display: 'inline-block',
            }}
            title={'Rewritten to another company — read-only' + (rw && rw.role === 'old' ? ` (new: ${rw.other_label})` : '')}
          >
            {STATUS_LABELS.rewritten}
          </span>
        ) : isLockedRenewed ? (
          <span
            style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600,
              background: statusColor, color: pillText, borderRadius: 3,
              display: 'inline-block',
            }}
            title={renewedTooltip}
          >
            ✓ {STATUS_LABELS[cycle.status] || cycle.status}
          </span>
        ) : canEditThisRow ? (
          <select value={cycle.status} onChange={(e) => onStatusChange(e.target.value)}
            style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600,
              background: statusColor, color: pillText, border: 'none', borderRadius: 3,
              cursor: 'pointer',
            }}>
            {PICKABLE_STATUSES.map((v) => <option key={v} value={v}>{STATUS_LABELS[v]}</option>)}
          </select>
        ) : (
          <span
            style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600,
              background: statusColor, color: pillText, borderRadius: 3,
              display: 'inline-block', opacity: 0.7,
            }}
            title="Read-only — assigned to another employee"
          >
            {STATUS_LABELS[cycle.status] || cycle.status}
          </span>
        )}
        {/* 5.28.0: Edit — every row except Hidden-section and rewritten rows; all users */}
        {!readOnly && !isRewritten && !pendingStatus && (
          <button data-testid={`edit-${cycle.id}`} onClick={() => setShowEdit(true)} title="Edit this renewal (correction or company change)"
            style={{ display: 'block', marginTop: 4, fontSize: 10, padding: '1px 8px', background: 'transparent', color: textSecondary,
              border: `1px solid ${borderColor}`, borderRadius: 3, cursor: 'pointer' }}>
            ✎ Edit
          </button>
        )}
      </td>
      {cycle.is_late_addition && (
        <td style={{ padding: '8px 4px', fontSize: 16 }} title="Late addition">⚠️</td>
      )}
      {linkingPolicyId && (
        <td style={{ padding: 0, position: 'absolute' }}>
          <LinkToClientModal
            policyId={linkingPolicyId}
            currentName={cycle.named_insured}
            currentClientId={cycle.client_id || null}
            currentClientName={cycle.client_name}
            darkMode={darkMode}
            onClose={() => setLinkingPolicyId(null)}
            onSaved={(data) => {
              setLinkingPolicyId(null);
              if (data?.trees_changed?.length && onClientTreesChanged) onClientTreesChanged(data.trees_changed);
              if (onChange) onChange();
            }}
          />
        </td>
      )}
      {showWorkspace && (
        <td style={{ padding: 0, position: 'absolute' }}>
          <RenewalWorkspace
            cycleId={cycle.id}
            title={cycle.named_insured}
            darkMode={darkMode}
            onClose={() => setShowWorkspace(false)}
            onSaved={() => { if (onChange) onChange(); }}
            onChanged={() => { if (onChange) onChange(); }}
            onClientTreesChanged={onClientTreesChanged}
          />
        </td>
      )}
      {showEdit && (
        <td style={{ padding: 0, position: 'absolute' }}>
          <RenewalEditModal
            cycle={cycle}
            darkMode={darkMode}
            onClose={() => setShowEdit(false)}
            onSaved={(res) => {
              // a Rewritten writes notes files into the client's Renewals/<year> -> refresh that tree
              if (res?.mode === 'rewritten' && cycle.client_id && onClientTreesChanged) onClientTreesChanged([cycle.client_id]);
              if (onChange) onChange();
            }}
          />
        </td>
      )}
      {showRenewModal && (
        <td style={{ padding: 0, position: 'absolute' }}>
          <RenewalModal
            cycle={cycle}
            users={users}
            darkMode={darkMode}
            onClose={() => setShowRenewModal(false)}
            onSaved={() => {
              setShowRenewModal(false);
              if (cycle.client_id && onClientTreesChanged) onClientTreesChanged([cycle.client_id]);   // Renewed notes file
              if (onChange) onChange();
            }}
          />
        </td>
      )}
    </tr>
  );
}

// =============================================================================
// Active list view (per-month renewals)
// =============================================================================

function ActiveList({ user, isAdminOrManager, onlyMine, openClientFolder, darkMode, panelBg, subPanelBg, borderColor, textPrimary, textSecondary, onClientTreesChanged }) {
  const monthOpts = buildMonthOptions();
  const now = new Date();
  const defaultIdx = monthOpts.findIndex((o) => o.year === now.getFullYear() && o.month === now.getMonth() + 1);

  const [pickIdx, setPickIdx] = useState(defaultIdx >= 0 ? defaultIdx : 0);
  const [data, setData] = useState({ year: 0, month: 0, is_finalized: false, counts: {}, cycles: [] });
  const [users, setUsers] = useState([]);
  const [pickGroups, setPickGroups] = useState({ active: [], hidden_with_open: [], former: [] });   // v5.27.0
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [printRows, setPrintRows] = useState([]);
  const [printScope, setPrintScope] = useState(null); // null | 'current' | 'all' (drives data + heading)
  const [printSeq, setPrintSeq] = useState(0);         // bumped per click → effect re-fires even if scope is unchanged
  // v4.19: read once per mount (lazy init). The My / All tabs mount separate
  // ActiveList instances, so each picks up whatever the other last persisted
  // when you switch to it — one layout per browser, not per tab.
  const [colWidths, setColWidths] = useState(loadColWidths);
  const [verifyOpen, setVerifyOpen] = useState(false);  // v4.20 collapsed by default
  const [hiddenOpen, setHiddenOpen] = useState(false);

  const resetColWidths = () => {
    setColWidths({ ...DEFAULT_COL_WIDTHS });
    clearColWidths();
  };

  const pick = monthOpts[pickIdx];
  // v4.20: split returned rows into the three sections. Identity split when the
  // response is not collapse-annotated → MAIN === data.cycles, others empty, so
  // active-arm / flag-off months are byte-identical to today.
  const { isCollapse, MAIN, VERIFY, HIDDEN } = partitionCycles(data.cycles);

  const load = () => {
    setLoading(true);
    const params = { year: pick.year, month: pick.month };
    // Future periods (beyond the current cycle year) have no prior-year
    // effective-date policies yet, so the standard active-list projection
    // returns nothing. For those, query entered cycles directly
    // (include_archive) so pre-entered next-year renewals are visible for
    // review / duplicate-checking. Current and past periods keep the stable
    // same-month-prior-year behavior unchanged.
    if (pick.year > now.getFullYear()) params.include_archive = true;
    // Compute effective filter at call time, not from stale state.
    // onlyMine forces the filter to user.id; otherwise honor manual selection.
    const effectiveFilter = onlyMine ? (user?.id || null) : filterUser;
    if (effectiveFilter) params.assigned_user_id = effectiveFilter;
    renewals.listCycles(params)
      .then((r) => setData(r.data))
      .catch((e) => console.error('listCycles failed', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // v5.27.0: one backend rule for every picker. Row/Renew pickers = active group, and
    // only for admin/manager (staff had no list before either).
    renewals.assignees().then((r) => {
      const g = { active: [], hidden_with_open: [], former: [], ...(r.data || {}) };
      setPickGroups(g);
      setUsers(isAdminOrManager ? g.active : []);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickIdx, filterUser, onlyMine, user?.id]);

  // Feature 1: print exactly what's shown in this instance (My / All / filtered).
  const handlePrintCurrent = () => {
    const rows = MAIN;                 // v4.20: print follows the MAIN list only
    if (!rows.length) return;          // nothing on screen → no-op (no empty sheet)
    setPrintRows(rows);
    setPrintScope('current');
    setPrintSeq((n) => n + 1);         // guarantees the print effect re-fires (no dead-click after a cancelled dialog)
  };

  // Feature 2: print the full all-staff list for this month, regardless of the
  // current tab/filter. Re-issues the SAME listCycles call the "All" tab makes
  // (no assigned_user_id) — presentation only; on-screen filter is untouched.
  const handlePrintAll = async () => {
    const params = { year: pick.year, month: pick.month };
    if (pick.year > now.getFullYear()) params.include_archive = true;
    try {
      const r = await renewals.listCycles(params);
      const rows = mainRowsOnly(r.data.cycles);   // v4.20: MAIN only (excludes VERIFY/HIDDEN)
      if (!rows.length) { alert('No renewals to print for this month.'); return; }
      setPrintRows(rows);
      setPrintScope('all');
      setPrintSeq((n) => n + 1);
    } catch (e) {
      alert('Print failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Once the print rows are committed to the DOM, open the dialog; reset after.
  // Keyed on printSeq (not printScope) so a re-click always re-fires, even if a
  // prior dialog was cancelled without an afterprint event.
  useEffect(() => {
    if (!printSeq) return;             // skip initial mount
    const reset = () => setPrintScope(null);
    window.addEventListener('afterprint', reset);
    window.print();
    return () => window.removeEventListener('afterprint', reset);
  }, [printSeq]);

  const finalize = async () => {
    if (!window.confirm(`Finalize the renewal list for ${pick.label}? After finalization, new entries become "late additions".`)) return;
    try {
      await renewals.finalizeMonth({ year: pick.year, month: pick.month });
      load();
    } catch (e) {
      alert('Finalize failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const counts = data.counts || {};
  const canManage = isAdminOrManager;

  // v4.20: a collapsed/expandable extra section (VERIFY / HIDDEN). Reuses the
  // SAME colWidths state, colgroup markup and CycleRow as MAIN, so columns stay
  // aligned; no resize handles here (resizing stays solely on MAIN's header).
  // sectionManage gates the editable controls: VERIFY passes canManage; HIDDEN
  // passes false so superseded historical terms are read-only.
  const renderCollapseSection = (title, rows, open, setOpen, sectionManage, sectionReadOnly = false) => (
    <div style={{ marginTop: 16 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '8px 10px', background: subPanelBg, color: textPrimary,
          border: `1px solid ${borderColor}`, borderRadius: 4, fontSize: 12,
          fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
        <span>{open ? '▾' : '▸'}</span><span>{title} ({rows.length})</span>
      </button>
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 6 }}>
          <table style={{ width: '100%', minWidth: tableMinWidth(colWidths),
            tableLayout: 'fixed', borderCollapse: 'collapse', background: panelBg }}>
            <colgroup>
              {ACTIVE_COLUMNS.map(c => <col key={c.id} style={{ width: colWidths[c.id] }} />)}
              <col />
            </colgroup>
            <thead>
              <tr style={{ background: subPanelBg, color: textSecondary, fontSize: 11, textAlign: 'left' }}>
                {ACTIVE_COLUMNS.map(c => (
                  <th key={c.id} style={{ padding: '8px 10px', fontWeight: 600, textAlign: c.align || 'left' }}>
                    <span style={{ display: 'block', ...CELL_CLIP }}>{c.label}</span>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <CycleRow key={c.id} cycle={c} users={users} canManage={sectionManage}
                  user={user} onChange={load} openClientFolder={openClientFolder}
                  darkMode={darkMode} panelBg={panelBg} borderColor={borderColor}
                  textPrimary={textPrimary} textSecondary={textSecondary} readOnly={sectionReadOnly}
                  onClientTreesChanged={onClientTreesChanged} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <select value={pickIdx} onChange={(e) => setPickIdx(parseInt(e.target.value, 10))}
          style={{ padding: '6px 10px', fontSize: 12, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 4 }}>
          {monthOpts.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
        </select>
        {canManage && !onlyMine && (
          <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}
            style={{ padding: '6px 10px', fontSize: 12, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 4 }}>
            <option value="">All employees</option>
            {pickGroups.active.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            {pickGroups.hidden_with_open.length > 0 && (
              <optgroup label="Hidden — still has renewals">
                {pickGroups.hidden_with_open.map(u => <option key={u.id} value={u.id}>{u.full_name} (hidden)</option>)}
              </optgroup>
            )}
            {pickGroups.former.length > 0 && (
              <optgroup label="Former employees">
                {pickGroups.former.map(u => <option key={u.id} value={u.id}>{u.full_name} (inactive)</option>)}
              </optgroup>
            )}
          </select>
        )}
        <button onClick={handlePrintCurrent}
          style={{ padding: '6px 12px', background: panelBg, color: '#185FA5', border: '1px solid #185FA5', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          🖨 Print renewals
        </button>
        <button onClick={handlePrintAll}
          style={{ padding: '6px 12px', background: panelBg, color: '#185FA5', border: '1px solid #185FA5', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          🖨 Print all (this month)
        </button>
        <button onClick={resetColWidths}
          title="Restore the default column widths (this browser only)"
          style={{ padding: '6px 10px', background: 'transparent', color: textSecondary, border: `1px solid ${borderColor}`, borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          ↔ Reset widths
        </button>
        <span style={{ flex: 1 }} />
        {canManage && !data.is_finalized && (
          <button onClick={finalize}
            style={{ padding: '6px 14px', background: '#27AE60', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🔒 Finalize Month
          </button>
        )}
        {data.is_finalized && (
          <span style={{ fontSize: 12, color: '#27AE60', fontWeight: 600 }}>✓ Finalized</span>
        )}
      </div>

      {/* Counts row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          ['Total', counts.total || 0, '#185FA5'],
          ['Pending', counts.pending || 0, STATUS_COLORS.pending],
          ['Contacted', counts.contacted || 0, STATUS_COLORS.contacted],
          ['Quoted', counts.quoted || 0, STATUS_COLORS.quoted],
          ['Renewed', counts.renewed || 0, STATUS_COLORS.renewed],
          ['Lost', counts.lost || 0, STATUS_COLORS.lost],
          ['Skipped', counts.skipped || 0, STATUS_COLORS.skipped],
          ['Duplicate', counts.duplicate || 0, STATUS_COLORS.duplicate],   // v4.21: cleanup progress meter
          ['Rewritten', counts.rewritten || 0, STATUS_COLORS.rewritten],   // 5.28.0: own pill, not in the total
        ].map(([label, n, color]) => (
          <div key={label} style={{ background: panelBg, padding: '6px 12px', borderRadius: 5, border: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, background: color, borderRadius: '50%' }} />
            <span style={{ fontSize: 11, color: textSecondary }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: textPrimary }}>{n}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>Loading…</div>
      ) : (data.cycles || []).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>No renewals for this month.</div>
      ) : (
        <>
        {MAIN.length === 0 ? (
          // v4.20: cycles exist but none are current-term — point staff at the
          // sections below instead of an empty table with only headers.
          <div style={{ textAlign: 'center', padding: 20, color: textSecondary }}>
            No current-term renewals this month — see the sections below.
          </div>
        ) : (
          // v4.19: the scroll container. Widening columns past the pane scrolls
          // horizontally instead of squashing the other columns.
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%', minWidth: tableMinWidth(colWidths), tableLayout: 'fixed',
              borderCollapse: 'collapse', background: panelBg,
            }}>
              <colgroup>
                {ACTIVE_COLUMNS.map(c => <col key={c.id} style={{ width: colWidths[c.id] }} />)}
                {/* ⚠️-marker column: no width → absorbs the slack (see MARKER_W). */}
                <col />
              </colgroup>
              <thead>
                <tr style={{ background: subPanelBg, color: textSecondary, fontSize: 11, textAlign: 'left' }}>
                  {ACTIVE_COLUMNS.map(c => (
                    <th key={c.id} style={{
                      padding: '8px 10px', fontWeight: 600,
                      textAlign: c.align || 'left', position: 'relative',
                    }}>
                      {/* The label clips inside the cell; the handle sits outside
                          it so a narrow column never hides its own grab zone. */}
                      <span style={{ display: 'block', ...CELL_CLIP }}>{c.label}</span>
                      <ColResizeHandle colId={c.id} widths={colWidths}
                        setWidths={setColWidths} accent="#185FA5" />
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {MAIN.map(c => (
                  <CycleRow key={c.id} cycle={c} users={users} canManage={canManage}
                    user={user}
                    onChange={load} openClientFolder={openClientFolder}
                    darkMode={darkMode} panelBg={panelBg} borderColor={borderColor}
                    textPrimary={textPrimary} textSecondary={textSecondary}
                    onClientTreesChanged={onClientTreesChanged} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isCollapse && VERIFY.length > 0 && renderCollapseSection('Needs verification', VERIFY, verifyOpen, setVerifyOpen, canManage, false)}
        {/* v4.21 (change C): HIDDEN now also holds owner-marked duplicates, which
            can occur on an active-arm (non-collapse) month too — render it
            whenever non-empty, not only when collapse annotations exist. */}
        {HIDDEN.length > 0 && renderCollapseSection('Hidden older terms & duplicates', HIDDEN, hiddenOpen, setHiddenOpen, false, true)}
        </>
      )}

      {/* Rendered via portal to document.body (a top-level sibling of #root,
          outside the live app tree) so @media print can hide the whole app at
          #root without taking the print table with it. State/props flow through
          the portal normally — only the DOM mount point moves. */}
      {printScope && createPortal(
        <div className="renewals-print-wrap">
          <h2 className="renewals-print-heading">
            Renewals — {pick.label}{printScope === 'all' ? ' · all staff' : ''}
          </h2>
          <table className="renewals-print">
            <thead>
              <tr>
                <th>Date</th><th>Insured</th><th>Address</th><th>Policy #</th><th>Carrier</th>
                <th className="num">Premium</th><th>Assigned</th><th>Note</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {printRows.map(c => (
                <tr key={c.id}>
                  <td className="expiration-date">{fmtDate(c.renewal_date)}</td>
                  <td className="client-name">{c.named_insured}{c.is_late_addition ? ' ⚠️' : ''}</td>
                  <td className="renewals-address">{formatAddress(c) || '—'}</td>
                  <td className="policy-number">{c.policy_number || '—'}</td>
                  <td>{c.carrier_name || '—'}</td>
                  <td className="premium num">{fmtMoney(c.premium)}</td>
                  <td>{assigneeLabel(c)}</td>
                  <td className="outcome-note">{c.outcome_notes || ''}</td>
                  <td>{STATUS_LABELS[c.status] || c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
        document.body
      )}
    </div>
  );
}

// =============================================================================
// Lapsed customers view
// =============================================================================

function LapsedView({ openClientFolder, darkMode, panelBg, subPanelBg, borderColor, textPrimary, textSecondary }) {
  const monthOpts = buildMonthOptions();
  const now = new Date();
  const defaultIdx = monthOpts.findIndex((o) => o.year === now.getFullYear() && o.month === now.getMonth() + 1);
  const [pickIdx, setPickIdx] = useState(defaultIdx >= 0 ? defaultIdx : 0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const pick = monthOpts[pickIdx];

  useEffect(() => {
    setLoading(true);
    renewals.lapsedCustomers({ year: pick.year, month: pick.month })
      .then((r) => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickIdx]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <select value={pickIdx} onChange={(e) => setPickIdx(parseInt(e.target.value, 10))}
          style={{ padding: '6px 10px', fontSize: 12, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 4 }}>
          {monthOpts.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
        </select>
        <span style={{ fontSize: 12, color: textSecondary }}>
          Customers who had renewals in past years but didn't renew with us in the prior year.
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}>{data.length} leads</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>No lapsed customers for this month.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: panelBg }}>
          <thead>
            <tr style={{ background: subPanelBg, color: textSecondary, fontSize: 11, textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Last Seen</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Insured</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Policy #</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Carrier</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Phone</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Address</th>
            </tr>
          </thead>
          <tbody>
            {data.map(p => (
              <tr key={p.id} style={{ borderTop: `1px solid ${borderColor}` }}>
                <td style={{ padding: '8px 10px', fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>{p.last_seen_cycle_year}</td>
                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>
                  {p.client_id ? (
                    <span style={{ color: '#5BA3D9', cursor: 'pointer' }} onClick={() => openClientFolder && openClientFolder(p.client_id)}>
                      {p.named_insured}
                    </span>
                  ) : p.named_insured}
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: textSecondary }}>{p.policy_number || '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{p.carrier_name || '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{p.phone || '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>
                  {[p.property_address_number, p.property_address_words, p.property_city, p.property_zip].filter(Boolean).join(' ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// =============================================================================
// Late additions view (admin)
// =============================================================================

function LateAdditionsView({ darkMode, panelBg, subPanelBg, borderColor, textPrimary, textSecondary, openClientFolder }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    renewals.listLateAdditions()
      .then((r) => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const review = async (id) => {
    try {
      await renewals.reviewLateAddition(id);
      load();
    } catch (e) {
      alert('Failed to review: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
      <div style={{ marginBottom: 12, fontSize: 12, color: textSecondary }}>
        Renewals added after their month was already finalized. Review to acknowledge.
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>No unreviewed late additions. ✓</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: panelBg }}>
          <thead>
            <tr style={{ background: subPanelBg, color: textSecondary, fontSize: 11, textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Renewal Date</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Insured</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Carrier</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Assigned</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${borderColor}` }}>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{fmtDate(c.renewal_date)}</td>
                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>
                  {c.client_id ? (
                    <span style={{ color: '#5BA3D9', cursor: 'pointer' }} onClick={() => openClientFolder && openClientFolder(c.client_id)}>
                      {c.named_insured}
                    </span>
                  ) : c.named_insured}
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{c.carrier_name || '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{assigneeLabel(c)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <button onClick={() => review(c.id)}
                    style={{ padding: '4px 12px', background: '#27AE60', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Acknowledge
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// =============================================================================
// v5.26.0: Former employees — open renewals assigned to inactive users
// =============================================================================
// All users. Real renewals (what the month lists work) drive the count and
// "Due soon"; imported 2-3-year projections sit in a collapsed section, also
// reassignable, never counted or flagged. Reassign = tick + "Reassign selected
// to…" an ACTIVE user; that policy's later open renewals still with the same
// former employee follow (backend). No automatic, split, "reassign all" or delete.

function FormerEmployeesView({ openClientFolder, onChanged, darkMode, panelBg, subPanelBg, borderColor, textPrimary, textSecondary }) {
  const [data, setData] = useState({ count: 0, cycles: [], projected: [], active_users: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [toUser, setToUser] = useState('');
  const [saving, setSaving] = useState(false);
  const [projOpen, setProjOpen] = useState(false);
  const [editCycle, setEditCycle] = useState(null);   // 5.28.0: Edit popup

  const load = () => {
    setLoading(true);
    renewals.formerEmployees()
      .then((r) => { setData(r.data); setSelected(new Set()); })
      .catch((e) => console.error('formerEmployees failed', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setTicked = (ids, on) => setSelected((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => { if (on) next.add(id); else next.delete(id); });
    return next;
  });

  const reassign = async () => {
    const target = data.active_users.find((u) => String(u.id) === String(toUser));
    if (!target || selected.size === 0) return;
    if (!window.confirm(`Reassign ${selected.size} renewal(s) to ${target.full_name}?\n\n`
      + 'Later open renewals of the same policies that are still with the same former employee move too.')) return;
    setSaving(true);
    try {
      const r = await renewals.reassignFormer({ cycle_ids: [...selected], to_user_id: target.id });
      const followed = r.data.count - selected.size;
      alert(`Reassigned ${r.data.count} renewal(s) to ${target.full_name}`
        + (followed > 0 ? ` (${followed} later-year renewal(s) followed).` : '.'));
      setToUser('');
      if (onChanged) onChanged();
    } catch (e) {
      alert('Reassign failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
      load();
    }
  };

  // Group by former employee (rows keep the backend's soonest-expiration order).
  const groupRows = (rows) => {
    const byUser = new Map();
    rows.forEach((c) => {
      if (!byUser.has(c.assigned_user_id)) byUser.set(c.assigned_user_id, { name: assigneeLabel(c), rows: [] });
      byUser.get(c.assigned_user_id).rows.push(c);
    });
    return [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name));
  };

  const th = { padding: '8px 10px', fontWeight: 600 };
  const renderTable = (rows) => {
    const ids = rows.map((c) => c.id);
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', background: panelBg }}>
        <thead>
          <tr style={{ background: subPanelBg, color: textSecondary, fontSize: 11, textAlign: 'left' }}>
            <th style={{ ...th, width: 30 }}>
              <input type="checkbox" checked={allOn} onChange={(e) => setTicked(ids, e.target.checked)}
                title="Tick / untick every renewal in this list" />
            </th>
            <th style={th}>Expiration</th>
            <th style={th}>Insured</th>
            <th style={th}>Policy #</th>
            <th style={th}>Carrier</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} style={{ borderTop: `1px solid ${borderColor}`, background: STATUS_ROW_TINT[c.status] || 'transparent' }}>
              <td style={{ padding: '8px 10px' }}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={(e) => setTicked([c.id], e.target.checked)} />
              </td>
              <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                <DueDate date={c.expiration_date || c.renewal_date} due={c.due_soon} />
              </td>
              <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>
                {c.client_id ? (
                  <span style={{ color: '#5BA3D9', cursor: 'pointer' }} onClick={() => openClientFolder && openClientFolder(c.client_id)}>
                    {c.named_insured}
                  </span>
                ) : c.named_insured}
              </td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: textSecondary }}>{c.policy_number || '—'}</td>
              <td style={{ padding: '8px 10px', fontSize: 12 }}>{c.carrier_name || '—'}</td>
              <td style={{ padding: '8px 10px', fontSize: 11 }}>
                <span style={{ padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                  background: STATUS_COLORS[c.status] || '#888', color: STATUS_PILL_TEXT[c.status] || '#fff' }}>
                  {STATUS_LABELS[c.status] || c.status}
                </span>
                {c.status !== 'rewritten' && (
                  <button data-testid={`edit-${c.id}`} onClick={() => setEditCycle(c)} title="Edit this renewal (correction or company change)"
                    style={{ marginLeft: 6, fontSize: 10, padding: '1px 8px', background: 'transparent', color: textSecondary,
                      border: `1px solid ${borderColor}`, borderRadius: 3, cursor: 'pointer' }}>✎ Edit</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const groups = groupRows(data.cycles || []);
  const projected = data.projected || [];

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
      {editCycle && (
        <RenewalEditModal cycle={editCycle} darkMode={darkMode}
          onClose={() => setEditCycle(null)}
          onSaved={() => { load(); if (onChanged) onChanged(); }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: textSecondary }}>
          Open renewals still assigned to employees who have left, or to logins hidden from renewals — all months,
          soonest first. They stay theirs until someone reassigns them.
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}>{selected.size} selected</span>
        <select value={toUser} onChange={(e) => setToUser(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 12, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 4 }}>
          <option value="">Reassign selected to…</option>
          {(data.active_users || []).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <button onClick={reassign} disabled={saving || !toUser || selected.size === 0}
          style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600,
            cursor: (saving || !toUser || selected.size === 0) ? 'default' : 'pointer', opacity: (saving || !toUser || selected.size === 0) ? 0.5 : 1 }}>
          {saving ? 'Reassigning…' : 'Reassign'}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>Loading…</div>
      ) : (
        <>
          {groups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: textSecondary }}>No renewals from former employees. ✓</div>
          ) : groups.map((g) => (
            <div key={g.name} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, margin: '6px 0' }}>
                {g.name} — {g.rows.length} renewal{g.rows.length === 1 ? '' : 's'}
              </div>
              {renderTable(g.rows)}
            </div>
          ))}
          {projected.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setProjOpen((o) => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '8px 10px', background: subPanelBg, color: textPrimary,
                  border: `1px solid ${borderColor}`, borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                <span>{projOpen ? '▾' : '▸'}</span><span>Projected (imported) ({projected.length})</span>
              </button>
              {projOpen && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: textSecondary, margin: '4px 0 8px' }}>
                    Imported 2–3-year projections (not in the month lists' working list). Not counted in the banner and
                    never marked “Due soon”; reassign them the same way if wanted.
                  </div>
                  {groupRows(projected).map((g) => (
                    <div key={g.name} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: textPrimary, margin: '6px 0' }}>
                        {g.name} — {g.rows.length}
                      </div>
                      {renderTable(g.rows)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Main Renewals component
// =============================================================================

// =============================================================================
// ManageCompaniesModal — admin-only: add / deactivate / restore carriers.
// Loads the FULL list (include_inactive:true) so deactivated companies stay
// visible here with a Restore action. That list is SEPARATE from the param-less
// (active-only) list the +Renewal/Renew dropdowns load on their own mount.
// "Remove" deactivates (is_active:false) — never a hard delete — so existing
// policies keep displaying the carrier name; it just drops out of the pick
// lists. Add/remove/restore re-fetch this modal's own list in place; the
// dropdowns pick up the change the next time they open (no auto-select).
// =============================================================================

function ManageCompaniesModal({ darkMode, onClose }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const subPanelBg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';

  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [busyId, setBusyId] = useState(null);        // carrier id currently toggling
  const [actionError, setActionError] = useState(null);

  // The manage modal's own list: ALL carriers incl. inactive (separate from the
  // dropdowns' active-only list).
  const fetchList = () =>
    renewals.listCarriers({ include_inactive: true })
      .then((r) => { setList(r.data || []); setListError(null); })
      .catch((e) => setListError(e.message || 'Failed to load companies.'))
      .finally(() => setLoadingList(false));

  useEffect(() => { fetchList(); }, []);

  const handleAdd = async () => {
    const clean = name.trim();
    if (!clean) { setAddError('Company name is required.'); return; }
    setAdding(true);
    setAddError(null);
    try {
      await renewals.createCarrier({ name: clean });
      setName('');
      await fetchList();
    } catch (e) {
      if (e.response?.status === 409) {
        setAddError(e.response?.data?.detail || 'That company already exists.');
      } else {
        let msg = e.response?.data?.detail;
        if (Array.isArray(msg)) msg = msg.map(d => d.msg).join('; ');
        setAddError(msg || e.message || 'Failed to add company.');
      }
    } finally {
      setAdding(false);
    }
  };

  // Remove = deactivate (is_active:false); Restore = is_active:true. Both go
  // through the admin-gated PATCH /carriers/{id}.
  const setActive = async (id, isActive) => {
    setBusyId(id);
    setActionError(null);
    try {
      await renewals.updateCarrier(id, { is_active: isActive });
      await fetchList();
    } catch (e) {
      setActionError(e.response?.data?.detail || e.message || 'Failed to update company.');
    } finally {
      setBusyId(null);
    }
  };

  const active = list.filter((c) => c.is_active);
  const inactive = list.filter((c) => !c.is_active);
  const busy = adding || busyId != null;

  const inputStyle = {
    flex: 1, padding: '8px 10px', fontSize: 13,
    background: inputBg, color: textPrimary,
    border: `1px solid ${borderColor}`, borderRadius: 5,
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 4, display: 'block' };
  const sectionHdr = { fontSize: 11, fontWeight: 700, color: textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, margin: '4px 0 6px' };
  const rowStyle = (greyed) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '7px 10px', borderRadius: 5, background: subPanelBg,
    marginBottom: 6, opacity: greyed ? 0.55 : 1,
  });
  const actionBtn = (color, isBusy) => ({
    padding: '4px 10px', background: 'transparent', color,
    border: `1px solid ${color}`, borderRadius: 4, fontSize: 11, fontWeight: 600,
    cursor: isBusy ? 'default' : 'pointer', opacity: isBusy ? 0.5 : 1,
  });

  return (
    <div onMouseDown={() => { if (!busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 460, maxHeight: '82vh', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#185FA5' }}>🏢 Manage companies</span>
        </div>

        {/* Add a company (the existing create flow) */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}` }}>
          <label style={labelStyle}>Add a company</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input autoFocus style={inputStyle} value={name}
              onChange={(e) => { setName(e.target.value); if (addError) setAddError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !adding) handleAdd(); }}
              placeholder="e.g. Allstate" disabled={adding} />
            <button onClick={handleAdd} disabled={adding}
              style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: adding ? 0.6 : 1 }}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
          {addError && <div style={{ marginTop: 8, fontSize: 12, color: '#c0392b' }}>{addError}</div>}
        </div>

        {/* Scrollable carrier list, sectioned active / deactivated */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
          {actionError && <div style={{ marginBottom: 8, fontSize: 12, color: '#c0392b' }}>{actionError}</div>}
          {loadingList ? (
            <div style={{ fontSize: 12, color: textSecondary }}>Loading…</div>
          ) : listError ? (
            <div style={{ fontSize: 12, color: '#c0392b' }}>{listError}</div>
          ) : (
            <>
              <div style={sectionHdr}>Active ({active.length})</div>
              {active.length === 0 && <div style={{ fontSize: 12, color: textSecondary, marginBottom: 8 }}>None.</div>}
              {active.map((c) => (
                <div key={c.id} style={rowStyle(false)}>
                  <span style={{ fontSize: 13 }}>{c.name}</span>
                  <button onClick={() => setActive(c.id, false)} disabled={busyId === c.id}
                    style={actionBtn('#c0392b', busyId === c.id)}>
                    {busyId === c.id ? '…' : 'Remove'}
                  </button>
                </div>
              ))}

              {inactive.length > 0 && (
                <>
                  <div style={{ ...sectionHdr, marginTop: 14 }}>Deactivated ({inactive.length})</div>
                  {inactive.map((c) => (
                    <div key={c.id} style={rowStyle(true)}>
                      <span style={{ fontSize: 13 }}>{c.name}</span>
                      <button onClick={() => setActive(c.id, true)} disabled={busyId === c.id}
                        style={actionBtn('#185FA5', busyId === c.id)}>
                        {busyId === c.id ? '…' : 'Restore'}
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '6px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Renewals({ user, darkMode, onClose, openClientFolder, onClientTreesChanged }) {
  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = isAdmin || user?.role === 'manager';

  const [tab, setTab] = useState('mine');
  const [showManageCompanies, setShowManageCompanies] = useState(false);
  const [formerCount, setFormerCount] = useState(0);   // v5.26.0 banner (all users)

  const loadFormerCount = () => {
    renewals.formerEmployeesCount().then((r) => setFormerCount(r.data?.count || 0)).catch(() => {});
  };
  useEffect(() => { loadFormerCount(); }, [tab]);

  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const subPanelBg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  const tabs = [
    { id: 'mine', label: 'My Renewals' },
    { id: 'all', label: 'All Renewals' },  // staff see read-only; admin/manager can edit any row
    { id: 'lapsed', label: 'Lapsed Customers' },
    ...(isAdmin ? [{ id: 'late', label: 'Late Additions' }] : []),
    { id: 'former', label: 'Former employees' },   // v5.26.0: all users
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: bg, color: textPrimary, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: panelBg, borderBottom: `1px solid ${borderColor}`, padding: '0 18px', display: 'flex', alignItems: 'center', height: 50, gap: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#185FA5' }}>📋 Renewals</span>
        <div style={{ display: 'flex', borderRadius: 5, overflow: 'hidden', border: `1px solid ${borderColor}`, marginLeft: 8 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '7px 16px', border: 'none',
                background: tab === t.id ? '#185FA5' : panelBg,
                color: tab === t.id ? '#fff' : textPrimary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
              {t.label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {isAdmin && (
          <button onClick={() => setShowManageCompanies(true)}
            style={{ padding: '7px 14px', background: panelBg, color: '#185FA5', border: `1px solid #185FA5`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Manage companies
          </button>
        )}
        <button onClick={onClose}
          style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
          Close
        </button>
      </div>

      {formerCount > 0 && tab !== 'former' && (
        <div onClick={() => setTab('former')} role="button"
          style={{ padding: '8px 18px', background: darkMode ? '#3a2a10' : '#FFF4D6', color: darkMode ? '#F5C76B' : '#7A5200',
            borderBottom: `1px solid ${borderColor}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          ⚠ {formerCount} renewal{formerCount === 1 ? '' : 's'} from former employees — reassign them →
        </div>
      )}

      {showManageCompanies && (
        <ManageCompaniesModal darkMode={darkMode} onClose={() => setShowManageCompanies(false)} />
      )}

      {tab === 'mine' && (
        <ActiveList user={user} isAdminOrManager={isAdminOrManager} onlyMine={true}
          openClientFolder={openClientFolder} darkMode={darkMode}
          panelBg={panelBg} subPanelBg={subPanelBg} borderColor={borderColor}
          textPrimary={textPrimary} textSecondary={textSecondary}
          onClientTreesChanged={onClientTreesChanged} />
      )}
      {tab === 'all' && (
        <ActiveList user={user} isAdminOrManager={isAdminOrManager} onlyMine={false}
          openClientFolder={openClientFolder} darkMode={darkMode}
          panelBg={panelBg} subPanelBg={subPanelBg} borderColor={borderColor}
          textPrimary={textPrimary} textSecondary={textSecondary}
          onClientTreesChanged={onClientTreesChanged} />
      )}
      {tab === 'lapsed' && (
        <LapsedView openClientFolder={openClientFolder} darkMode={darkMode}
          panelBg={panelBg} subPanelBg={subPanelBg} borderColor={borderColor}
          textPrimary={textPrimary} textSecondary={textSecondary} />
      )}
      {tab === 'former' && (
        <FormerEmployeesView openClientFolder={openClientFolder} onChanged={loadFormerCount} darkMode={darkMode}
          panelBg={panelBg} subPanelBg={subPanelBg} borderColor={borderColor}
          textPrimary={textPrimary} textSecondary={textSecondary} />
      )}
      {tab === 'late' && (
        <LateAdditionsView openClientFolder={openClientFolder} darkMode={darkMode}
          panelBg={panelBg} subPanelBg={subPanelBg} borderColor={borderColor}
          textPrimary={textPrimary} textSecondary={textSecondary} />
      )}
    </div>
  );
}
